import { ChangeDetectionStrategy, Component, computed, DestroyRef, ElementRef, inject, signal, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { CloudApiService } from '../../services/cloud-api.service';
import { BREADCRUMB_HOME, BreadcrumbsComponent } from '@/shared/breadcrumbs/breadcrumbs.component';
import { CONTROL_SYSTEM_LABEL, controlSystemDisplayName, linkedDeviceDisplayName, locationRefName } from '@/cloud/services/device-display';
import { TranslationService } from '@/layout/service/translation.service';
import { EmptyStateComponent, SectionCardComponent, StatusBadgeComponent, StatusDescriptor, status } from '@/shared/ui';
import { MessagesPanelComponent } from '@/shared/messages-panel/messages-panel.component';
import { deriveLinkedDeviceStatus } from '@/cloud/models/device-status';
import { parseMessages } from 'src/api/messages';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { ButtonModule } from 'primeng/button';
import { SkeletonModule } from 'primeng/skeleton';
import { MessageModule } from 'primeng/message';
import { DialogModule } from 'primeng/dialog';
import { TabsModule } from 'primeng/tabs';
import { SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { ToggleSwitch } from 'primeng/toggleswitch';
import { DividerModule } from 'primeng/divider';
import { ProgressBarModule } from 'primeng/progressbar';
import { ScrollPanelModule } from 'primeng/scrollpanel';
import { LocationPickerComponent } from '../../components/location-picker/location-picker.component';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import moment from 'moment';
import { LinkedDevicePowerComponent } from './linked-device-power.component';

const DEBUG_MODE_TIMEOUT_MINUTES = 60;
const DEBUG_LEVEL_NAMES: Record<number, string> = { 0: 'Debug', 1: 'Info', 2: 'Warning', 3: 'Error' };
const TABS = ['overview', 'outlets', 'commands', 'diagnostics'] as const;
type LinkedDeviceTab = (typeof TABS)[number];

export interface DebugLogEntry {
    level: 'Debug' | 'Info' | 'Warning' | 'Error';
    message: string;
    source: string;
    timestampUtc: string;
}

@Component({
    selector: 'app-linked-device-detail',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        RouterLink,
        BreadcrumbsComponent,
        SectionCardComponent,
        StatusBadgeComponent,
        EmptyStateComponent,
        MessagesPanelComponent,
        TagModule,
        TooltipModule,
        ButtonModule,
        SkeletonModule,
        MessageModule,
        DialogModule,
        TabsModule,
        SelectModule,
        InputTextModule,
        InputNumberModule,
        ToggleSwitch,
        DividerModule,
        ProgressBarModule,
        ScrollPanelModule,
        LocationPickerComponent,
        ToastModule,
        LinkedDevicePowerComponent
    ],
    providers: [MessageService],
    templateUrl: './linked-device-detail.html',
    styleUrl: './linked-device-detail.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class LinkedDeviceDetail implements OnInit, OnDestroy {
    private route = inject(ActivatedRoute);
    private router = inject(Router);
    private api = inject(CloudApiService);
    private messageService = inject(MessageService);
    private destroyRef = inject(DestroyRef);
    private queryParams = toSignal(this.route.queryParamMap, { initialValue: this.route.snapshot.queryParamMap });
    readonly t = inject(TranslationService).t;

    @ViewChild('logContainer') logContainer!: ElementRef<HTMLDivElement>;

    device = signal<any>(null);
    loading = signal(true);
    error = signal<string | null>(null);
    /** Tab from `?tab=`; a tab the device doesn't have (or the former Control tab) opens Overview. */
    activeTab = computed<LinkedDeviceTab>(() => {
        const tab = this.queryParams().get('tab');
        if (!(TABS as readonly string[]).includes(tab ?? '')) return 'overview';
        if (tab === 'outlets' && !this.hasOutlets()) return 'overview';
        if (tab === 'commands' && !this.hasCommands()) return 'overview';
        if (tab === 'diagnostics' && !this.hasDiagnostics()) return 'overview';
        return tab as LinkedDeviceTab;
    });
    tunnelConnected = signal<boolean | null>(null); // null = checking, true/false = known

    private debugCountdownInterval: ReturnType<typeof setInterval> | null = null;
    /** Timestamp (ms) of when we last anchored the countdown */
    private debugCountdownAnchorTime: number | null = null;
    /** Remaining minutes at anchor time — countdown ticks from here */
    private debugCountdownAnchorMinutes: number | null = null;

    breadcrumbHome = BREADCRUMB_HOME;

    /** What the portal calls the processor this device hangs off. */
    readonly controlSystemLabel = CONTROL_SYSTEM_LABEL;

    /** Template helper — see linkedDeviceDisplayName for the precedence. */
    displayName = linkedDeviceDisplayName;

    /**
     * Name of the parent control system. The API resolves `parentDeviceName` through the same
     * precedence the device pages use, so this only has to cover a response that predates that.
     */
    parentDeviceName = computed(() => {
        const d = this.device();
        return controlSystemDisplayName(d?.parentDeviceName, d?.parentDeviceId);
    });

    /** Name of the location this device sits in, never its id. */
    locationLabel = computed(() => {
        const d = this.device();
        if (!d) return '—';
        return locationRefName(d.locationName, d.locationNodeId);
    });

    breadcrumbItems = computed(() => {
        const d = this.device();
        if (!d) return [];
        const items: { label: string; routerLink: any[] | string }[] = [];

        const nsId = d.appNamespaceId;
        if (nsId) {
            const namespaces = this.api.namespaces();
            const ns = namespaces?.find((n: any) => n.id === nsId);
            if (ns) {
                items.push({ label: ns.name, routerLink: ['/dashboard', nsId] });
            }
        }

        if (nsId && d.parentDeviceLocationNodeId) {
            const locations = this.api.locations();
            if (locations) {
                const locsInNs = locations.filter((l: any) => l.appNamespaceId === nsId);
                const map = new Map<string, any>();
                locsInNs.forEach((l: any) => map.set(l.id, l));
                const chain: any[] = [];
                let cursor = map.get(d.parentDeviceLocationNodeId);
                let guard = 0;
                while (cursor && guard++ < 50) {
                    chain.unshift(cursor);
                    cursor = cursor.parentId ? map.get(cursor.parentId) : undefined;
                }
                chain.forEach((c) => items.push({ label: c.name, routerLink: ['/dashboard', nsId, c.id] }));
            }
        }

        if (d.parentDeviceId) {
            items.push({ label: this.parentDeviceName(), routerLink: ['/device', d.parentDeviceId] });
            items.push({ label: this.t()('linked.breadcrumb'), routerLink: ['/device', d.parentDeviceId, 'linked-devices'] });
        }

        items.push({ label: linkedDeviceDisplayName(d), routerLink: ['/linked-device', d.id] });
        return items;
    });

    // Move dialog
    showMoveDialog = signal(false);
    availableLocations = signal<any[]>([]);
    selectedTargetLocation = signal<string | null>(null);
    moveSubmitting = signal(false);
    moveError = signal<string | null>(null);

    // Remove dialog
    showRemoveDialog = signal(false);
    removeSubmitting = signal(false);
    removeError = signal<string | null>(null);

    // Outlets tab - power distribution devices (PDUs)
    hasOutlets = computed(() => !!this.device()?.capabilities?.hasOutlets);

    /** A touch panel: reports its IP ID and UI connections, and supports health and debug but no control. */
    isPanel = computed(() => this.device()?.kind === 'panel');

    /**
     * Whether the control system can be asked about this device: drivers and touch panels. Other IP ID
     * devices are listed for their online state only. An API from before `kind` sends only the runtime id.
     */
    hasDiagnostics = computed(() => {
        const d = this.device();
        if (!d) return false;
        if (d.kind === 'panel') return true;
        if (d.kind === 'cip') return false;
        return d.runtimeDeviceId != null;
    });

    /** The panel's IP ID and UI connection state (check-in, refreshed live by getState). */
    panelConnections = computed(() => {
        const d = this.device();
        if (d?.kind !== 'panel') return null;
        const s = d.state ?? {};
        const uiRemote: string[] = Array.isArray(s.uiRemoteAddresses) ? s.uiRemoteAddresses : [];
        return {
            ipId: s.ipId ?? '',
            ipIdOnline: !!(s.ipIdOnline ?? d.online),
            ipAddresses: (Array.isArray(s.ipAddresses) ? s.ipAddresses : []) as string[],
            expectsUi: s.expectsUiConnection !== false,
            uiConnectionCount: Number(s.uiConnectionCount ?? 0),
            uiRemoteAddresses: uiRemote,
            uiAddress: (s.uiAddress as string) || '',
            loadedProject: (s.loadedProject as string) || ''
        };
    });

    // Live control (Overview). Processors report state for power, standby and input only.
    hasControlCapabilities = computed(() => {
        const caps = this.device()?.capabilities;
        return !!caps && !!(caps.hasPower || caps.hasStandby || caps.hasInputControl);
    });
    /** Why live control is unavailable, or null when it can be used. */
    controlBlockedReason = computed(() => {
        const d = this.device();
        if (this.tunnelConnected() === false) return this.t()('linked.control.unavailableTunnel');
        if (!d?.online || d?.missing) return this.t()('linked.control.unavailableOffline');
        return null;
    });

    statusDescriptor = computed<StatusDescriptor>(() => {
        const d = this.device();
        const s = deriveLinkedDeviceStatus(d);
        // An error-health device shares the warning key; label it "Error" as the list views do.
        const labelKey = s.key === 'warning' && d?.health === 'error' ? 'error' : s.key;
        return status(s.key, this.t()(`linked.status.${labelKey}`));
    });

    /** Icon for the kind of device, from its capabilities and driver type. */
    deviceIcon = computed(() => {
        const d = this.device();
        const caps = d?.capabilities ?? {};
        const type = String(d?.type ?? '').toLowerCase();
        if (d?.kind === 'panel' || caps.isTouchPanel) return 'pi pi-tablet';
        if (caps.hasOutlets) return 'fa fa-plug-circle-bolt';
        if (caps.hasCamera || type.includes('camera')) return 'pi pi-camera';
        if (type.includes('codec')) return 'pi pi-video';
        if (caps.hasInputControl || /display|projector|monitor|tv/.test(type)) return 'pi pi-desktop';
        if (caps.hasVolume || caps.hasMute || /audio|dsp|amp|speaker|mic/.test(type)) return 'pi pi-volume-up';
        return 'pi pi-box';
    });

    makeModel = computed(() => {
        const d = this.device();
        return [d?.manufacturerName, d?.modelName].filter(Boolean).join(' ');
    });

    /** Driver warnings and errors, read from the control system over the tunnel. */
    healthMessages = computed(() => parseMessages(this.diagnosticMessages()).filter((m) => m.level === 'error' || m.level === 'warn'));

    controlExecuting = signal<string | null>(null);

    // Commands tab
    hasCommands = computed(() => (this.device()?.commands?.length ?? 0) > 0);
    selectedCommand = signal<string | null>(null);
    commandArgs: Record<string, any> = {};
    commandExecuting = signal(false);
    commandResult = signal<{ success: boolean; message: string; data?: any } | null>(null);

    commandOptions = computed(() => {
        const commands = this.device()?.commands ?? [];
        const categoryMap = new Map<string, any[]>();
        for (const cmd of commands) {
            const category = cmd.category || this.t()('linked.commands.general');
            if (!categoryMap.has(category)) categoryMap.set(category, []);
            categoryMap.get(category)!.push(cmd);
        }
        return Array.from(categoryMap.entries())
            .sort((a, b) => a[0].localeCompare(b[0]))
            .map(([category, cmds]) => ({
                label: category,
                items: cmds.map((cmd: any) => ({ label: cmd.name, value: cmd.name, description: cmd.description }))
            }));
    });

    selectedCommandDetails = computed(() => {
        const cmdName = this.selectedCommand();
        if (!cmdName) return null;
        return (this.device()?.commands ?? []).find((c: any) => c.name === cmdName) || null;
    });

    // Diagnostics tab
    debugEnabled = signal(false);
    debugToggling = signal(false);
    debugTimeRemainingMinutes = signal<number | null>(null);
    debugLogs = signal<DebugLogEntry[]>([]);
    diagnosticMessages = signal<any[]>([]);
    debugLogsLoading = signal(false);

    debugTimeRemainingPercent = computed(() => {
        const mins = this.debugTimeRemainingMinutes();
        if (mins == null) return 0;
        return Math.max(0, Math.min(100, (mins / DEBUG_MODE_TIMEOUT_MINUTES) * 100));
    });

    debugTimeRemainingText = computed(() => {
        const mins = this.debugTimeRemainingMinutes();
        if (mins == null) return '';
        if (mins < 1) return this.t()('linked.debug.lessThanMinute');
        return this.t()('linked.debug.remaining').replace('{n}', String(Math.floor(mins)));
    });

    async ngOnInit() {
        const linkedDeviceId = this.route.snapshot.paramMap.get('linkedDeviceId');
        if (!linkedDeviceId) {
            this.error.set(this.t()('linked.noId'));
            this.loading.set(false);
            return;
        }
        await this.loadDevice(linkedDeviceId);
        this.checkTunnelStatus();
        this.setupRealtimeSubscriptions();
        // Fetch live state from device for accurate debug countdown (non-blocking)
        this.refreshDiagnosticsState();
    }

    ngOnDestroy() {
        this.stopDebugCountdown();
    }

    onTabChange(tab: string | number | undefined) {
        const tabValue = String(tab ?? 'overview');
        if (!(TABS as readonly string[]).includes(tabValue)) return;
        void this.router.navigate([], { relativeTo: this.route, queryParams: { tab: tabValue === 'overview' ? null : tabValue }, queryParamsHandling: 'merge' });
        // Overview shows health messages and Diagnostics the debug state: both read live state.
        if (tabValue === 'diagnostics' || tabValue === 'overview') {
            this.refreshDiagnosticsState();
        }
    }

    private async refreshDiagnosticsState() {
        const d = this.device();
        if (!d || !this.hasDiagnostics()) return;
        try {
            const result = await this.api.executeLinkedDeviceAction(d.id, 'getState');
            if (result) {
                // State fields may be at top level or nested in a 'state' property
                const state = result.state ?? result;
                const serverEnabled = state.debugEnabled;
                const serverRemaining = state.debugTimeRemainingMinutes;

                if (serverEnabled !== undefined) {
                    this.debugEnabled.set(serverEnabled);
                }

                if (serverEnabled) {
                    const mins = serverRemaining ?? DEBUG_MODE_TIMEOUT_MINUTES;
                    if (this.debugCountdownInterval) {
                        // Timer already running — just re-anchor with fresh server value
                        this.resetCountdownAnchor(mins);
                    } else {
                        // Timer not running — start it with server value
                        this.startDebugCountdown(mins);
                    }
                } else {
                    this.debugTimeRemainingMinutes.set(null);
                    this.stopDebugCountdown();
                }

                if (result.messages) {
                    this.diagnosticMessages.set(result.messages);
                }
                // Update device state too
                if (state) {
                    this.device.update((current) => (current ? { ...current, state: { ...(current.state ?? {}), ...state } } : current));
                }
            }
        } catch {
            // Silently fail — state will update on next check-in
        }
    }

    async loadDevice(linkedDeviceId: string) {
        this.loading.set(true);
        this.error.set(null);
        try {
            const device = await this.api.getLinkedDevice(linkedDeviceId);
            this.device.set(device);
            this.debugEnabled.set(device.debugEnabled ?? false);
            if (device.messages) this.diagnosticMessages.set(device.messages);
            // Don't start countdown from Firestore data — it doesn't have remaining time.
            // The countdown will be started by refreshDiagnosticsState when the diagnostics tab is opened,
            // which fetches the actual remaining time from the device via the tunnel.
        } catch (err: any) {
            this.error.set(err?.error?.detail || err?.message || this.t()('linked.loadFailed'));
        } finally {
            this.loading.set(false);
        }
    }

    private async checkTunnelStatus() {
        const d = this.device();
        if (!d?.parentDeviceId) {
            this.tunnelConnected.set(false);
            return;
        }
        try {
            const status = await this.api.getDeviceTunnelStatus(d.parentDeviceId);
            this.tunnelConnected.set(status.tunnelConnected);
        } catch {
            this.tunnelConnected.set(false);
        }
    }

    private startDebugCountdown(remainingMinutes?: number) {
        this.stopDebugCountdown();
        const initial = remainingMinutes ?? this.debugTimeRemainingMinutes() ?? DEBUG_MODE_TIMEOUT_MINUTES;
        this.debugCountdownAnchorTime = Date.now();
        this.debugCountdownAnchorMinutes = initial;
        this.debugTimeRemainingMinutes.set(initial);
        this.debugCountdownInterval = setInterval(() => {
            if (this.debugCountdownAnchorTime == null || this.debugCountdownAnchorMinutes == null) return;
            const elapsedMinutes = (Date.now() - this.debugCountdownAnchorTime) / 60_000;
            const remaining = Math.max(0, this.debugCountdownAnchorMinutes - elapsedMinutes);
            this.debugTimeRemainingMinutes.set(remaining);
            if (remaining <= 0) {
                this.debugEnabled.set(false);
                this.debugTimeRemainingMinutes.set(null);
                this.stopDebugCountdown();
            }
        }, 5_000); // tick every 5 seconds for responsive progress bar
    }

    /** Reset the countdown anchor to a new server-provided remaining time without restarting the interval */
    private resetCountdownAnchor(remainingMinutes: number) {
        this.debugCountdownAnchorTime = Date.now();
        this.debugCountdownAnchorMinutes = remainingMinutes;
        this.debugTimeRemainingMinutes.set(remainingMinutes);
    }

    private stopDebugCountdown() {
        if (this.debugCountdownInterval) {
            clearInterval(this.debugCountdownInterval);
            this.debugCountdownInterval = null;
        }
        this.debugCountdownAnchorTime = null;
        this.debugCountdownAnchorMinutes = null;
    }

    /**
     * Whether a live linked-device event is about this device. Processors name the device by uniqueId; only
     * events from firmware that predates that fall back to the runtime id, which a touch panel never has.
     */
    matchesEvent(event: { linkedUniqueId?: string | null; linkedDeviceId?: string | number | null } | null | undefined): boolean {
        const d = this.device();
        if (!d || !event) return false;
        if (event.linkedUniqueId) return event.linkedUniqueId === d.uniqueId;
        if (event.linkedDeviceId == null) return false;
        const runtimeId = d.runtimeDeviceId?.toString();
        return event.linkedDeviceId === d.id || (!!runtimeId && event.linkedDeviceId.toString() === runtimeId);
    }

    private setupRealtimeSubscriptions() {
        const d = this.device();
        if (!d?.parentDeviceId) return;

        // Tunnel status updates for parent device
        this.api
            .subscribeToDevice(d.parentDeviceId)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((parentDevice) => {
                if (parentDevice && (parentDevice as any).tunnelConnected !== undefined) {
                    this.tunnelConnected.set(!!(parentDevice as any).tunnelConnected);
                }
            });

        // State updates
        this.api
            .subscribeToLinkedDeviceStateUpdates(d.parentDeviceId)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((event) => {
                if (!event) return;
                if (this.matchesEvent(event) && event.state) {
                    this.device.update((current) => {
                        if (!current) return current;
                        const stateData = typeof event.state === 'object' ? event.state : {};
                        return {
                            ...current,
                            state: current.state ? { ...current.state, ...stateData } : stateData,
                            capabilities: stateData.capabilities ? { ...(current.capabilities ?? {}), ...stateData.capabilities } : current.capabilities
                        };
                    });

                    // Update debug state from state updates
                    const stateData = typeof event.state === 'object' ? event.state : {};
                    this.applyDebugState(stateData);
                }
            });

        // Command results
        this.api
            .subscribeToLinkedDeviceCommandResults(d.parentDeviceId)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((event) => {
                if (!event) return;
                if (this.matchesEvent(event)) {
                    // Result data may be at top level or nested in result
                    const resultData = event.result ?? event;
                    this.commandResult.set({
                        success: resultData.success ?? event.success,
                        message: resultData.message ?? event.message,
                        data: resultData.data ?? event.data
                    });
                    this.commandExecuting.set(false);
                    this.controlExecuting.set(null);

                    // Update debug state if command was debug-related
                    this.applyDebugState(resultData);
                }
            });

        // Debug logs — live streaming via pub/sub
        this.api
            .subscribeToLinkedDeviceDebugLogs(d.parentDeviceId)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((event) => {
                if (!this.matchesEvent(event)) return;

                // The log data may be nested inside event.log or at top level
                const logData = event.log ?? event;
                this.addLogEntry(logData);
            });
    }

    /** Normalize a log entry from pub/sub (handles integer or string level, PascalCase or camelCase) */
    private addLogEntry(logData: any) {
        // Handle both camelCase and PascalCase field names
        const rawLevel = logData.level ?? logData.Level;
        const message = logData.message ?? logData.Message;
        const source = logData.source ?? logData.Source ?? '';
        const timestampUtc = logData.timestampUtc ?? logData.TimestampUtc;

        if (message == null || timestampUtc == null) return;

        // Level may be string ("Debug") or integer (0) — normalize to string
        let level: string;
        if (typeof rawLevel === 'number') {
            level = DEBUG_LEVEL_NAMES[rawLevel] ?? 'Debug';
        } else {
            level = rawLevel ?? 'Debug';
        }

        const entry: DebugLogEntry = { level: level as DebugLogEntry['level'], message, source, timestampUtc };
        this.debugLogs.update((logs) => [...logs, entry]);
        this.scrollToBottom();
    }

    /** Apply debug state from server response or pub/sub event. Syncs enabled flag and countdown timer. */
    private applyDebugState(data: any) {
        if (data?.debugEnabled === undefined) return;

        const wasEnabled = this.debugEnabled();
        this.debugEnabled.set(data.debugEnabled);

        if (data.debugEnabled) {
            const mins = data.debugTimeRemainingMinutes ?? DEBUG_MODE_TIMEOUT_MINUTES;
            if (!wasEnabled) {
                // Just enabled — start fresh countdown
                this.startDebugCountdown(mins);
            } else if (this.debugCountdownInterval) {
                // Already running — re-anchor with server value
                this.resetCountdownAnchor(mins);
            } else {
                // Enabled but no timer running (shouldn't happen, but recover)
                this.startDebugCountdown(mins);
            }
        } else {
            this.stopDebugCountdown();
            this.debugTimeRemainingMinutes.set(null);
        }
    }

    // -- Formatting --

    formatLastSeen(): string {
        const d = this.device();
        if (!d) return '';
        if (d.online && !d.missing) return this.t()('linked.onlineNow');
        const m = d.lastSeenUtc ? moment.utc(d.lastSeenUtc) : null;
        return m?.isValid() ? m.fromNow() : this.t()('linked.neverSeen');
    }

    formatSyncedUtc(): string {
        const d = this.device();
        const m = d?.syncedUtc ? moment.utc(d.syncedUtc) : null;
        return m?.isValid() ? m.fromNow() : this.t()('linked.never');
    }

    /** Exact time for a tooltip next to a relative one. */
    formatExact(utc: string | null | undefined): string {
        const m = utc ? moment.utc(utc) : null;
        return m?.isValid() ? m.local().format('LLL') : '';
    }

    formatTime(utc: string): string {
        const date = new Date(utc);
        return date.toLocaleTimeString();
    }

    powerStatusSeverity(status: string | undefined): 'success' | 'danger' | 'warn' | 'info' | 'secondary' {
        switch (status) {
            case 'PowerOn':
                return 'success';
            case 'PowerOff':
                return 'danger';
            case 'PowerWarming':
                return 'warn';
            case 'PowerCooling':
                return 'info';
            default:
                return 'secondary';
        }
    }

    powerStatusLabel(status: string | undefined): string {
        switch (status) {
            case 'PowerOn':
            case 'PowerOff':
            case 'PowerWarming':
            case 'PowerCooling':
                return this.t()(`linked.power.${status}`);
            default:
                return status || this.t()('linked.power.unknown');
        }
    }

    // -- Control actions --

    async executeControl(action: string, params?: Record<string, any>) {
        const d = this.device();
        if (!d || this.controlBlockedReason()) return;
        this.controlExecuting.set(action);
        try {
            await this.api.executeLinkedDeviceAction(d.id, action, params);
            this.messageService.add({ severity: 'success', summary: this.t()('linked.control.sent') });
        } catch (err: any) {
            this.messageService.add({ severity: 'error', summary: this.t()('linked.control.failed'), detail: err?.error?.detail || err?.message });
        } finally {
            this.controlExecuting.set(null);
        }
    }

    togglePower() {
        const state = this.device()?.state;
        const powerOn = state?.powerStatus !== 'PowerOn';
        this.executeControl('setPower', { power: powerOn });
    }

    selectInput(input: string) {
        this.executeControl('setInput', { input });
    }

    toggleStandby() {
        const inStandby = this.device()?.state?.inStandby;
        this.executeControl('setStandby', { sleep: !inStandby });
    }

    // -- Commands --

    onCommandSelected(commandName: string | null) {
        this.selectedCommand.set(commandName);
        this.commandArgs = {};
        this.commandResult.set(null);
        const cmd = this.selectedCommandDetails();
        if (cmd) {
            for (const arg of cmd.arguments) {
                if (arg.defaultValue !== undefined) {
                    this.commandArgs[arg.name] = arg.defaultValue;
                }
            }
        }
    }

    async executeCommand() {
        const cmd = this.selectedCommandDetails();
        const d = this.device();
        if (!cmd || !d) return;

        for (const arg of cmd.arguments) {
            const value = this.commandArgs[arg.name];
            if (arg.required && (value === undefined || value === null || value === '')) {
                this.messageService.add({ severity: 'warn', summary: this.t()('linked.commands.missingField'), detail: this.t()('linked.commands.missingFieldDetail').replace('{label}', arg.label) });
                return;
            }
        }

        const args: Record<string, any> = {};
        for (const arg of cmd.arguments) {
            const value = this.commandArgs[arg.name];
            if (value !== undefined && value !== null && value !== '') {
                args[arg.name] = value;
            }
        }

        this.commandExecuting.set(true);
        this.commandResult.set(null);
        try {
            const result = await this.api.executeLinkedDeviceAction(d.id, 'command', { commandName: cmd.name, arguments: args });
            this.commandResult.set({ success: result?.success !== false, message: result?.message || this.t()('linked.commands.done'), data: result?.data });
        } catch (err: any) {
            this.commandResult.set({ success: false, message: err?.error?.detail || err?.message || this.t()('linked.commands.error') });
        } finally {
            this.commandExecuting.set(false);
        }
    }

    // -- Diagnostics --

    async toggleDebug() {
        const d = this.device();
        if (!d) return;
        const newValue = !this.debugEnabled();
        this.debugToggling.set(true);
        try {
            await this.api.executeLinkedDeviceAction(d.id, 'setDebug', { enabled: newValue });
            this.debugEnabled.set(newValue);
            if (newValue) {
                this.debugLogs.set([]);
                this.startDebugCountdown(DEBUG_MODE_TIMEOUT_MINUTES);
            } else {
                this.debugTimeRemainingMinutes.set(null);
                this.stopDebugCountdown();
            }
            this.messageService.add({ severity: 'success', summary: this.t()(newValue ? 'linked.debug.turnedOn' : 'linked.debug.turnedOff') });
        } catch (err: any) {
            this.messageService.add({ severity: 'error', summary: this.t()('linked.debug.toggleFailed'), detail: err?.error?.detail || err?.message });
        } finally {
            this.debugToggling.set(false);
        }
    }

    async refreshDebugLogs() {
        const d = this.device();
        if (!d) return;
        this.debugLogsLoading.set(true);
        try {
            const result = await this.api.executeLinkedDeviceAction(d.id, 'getDebugLogs');
            if (result?.logs) {
                this.debugLogs.set(result.logs);
                this.scrollToBottom(true);
            }
        } catch (err: any) {
            this.messageService.add({ severity: 'error', summary: this.t()('linked.debug.refreshFailed') });
        } finally {
            this.debugLogsLoading.set(false);
        }
    }

    clearDebugLogs() {
        this.debugLogs.set([]);
    }

    trackByTimestamp(_index: number, log: DebugLogEntry): string {
        return log.timestampUtc;
    }

    private scrollToBottom(force = false) {
        setTimeout(() => {
            if (this.logContainer?.nativeElement) {
                const container = this.logContainer.nativeElement.closest('.p-scrollpanel-content') as HTMLElement;
                if (container) {
                    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
                    if (force || distanceFromBottom < 100) {
                        container.scrollTop = container.scrollHeight;
                    }
                }
            }
        });
    }

    // -- Move dialog --

    async openMoveDialog() {
        this.selectedTargetLocation.set(null);
        this.moveError.set(null);
        try {
            const locations = await this.api.getLocations();
            this.availableLocations.set(locations || []);
        } catch {
            this.availableLocations.set([]);
        }
        this.showMoveDialog.set(true);
    }

    async confirmMove() {
        const d = this.device();
        const targetId = this.selectedTargetLocation();
        if (!d || !targetId) return;
        this.moveSubmitting.set(true);
        this.moveError.set(null);
        try {
            await this.api.moveLinkedDevice(d.id, targetId);
            this.messageService.add({ severity: 'success', summary: this.t()('linked.moveDialog.done'), detail: this.t()('linked.moveDialog.doneDetail').replace('{name}', linkedDeviceDisplayName(d)) });
            this.showMoveDialog.set(false);
            await this.loadDevice(d.id);
        } catch (err: any) {
            this.moveError.set(err?.error?.detail || this.t()('linked.moveDialog.failed'));
        } finally {
            this.moveSubmitting.set(false);
        }
    }

    // -- Remove dialog --

    openRemoveDialog() {
        this.removeError.set(null);
        this.showRemoveDialog.set(true);
    }

    async confirmRemove() {
        const d = this.device();
        if (!d) return;
        this.removeSubmitting.set(true);
        this.removeError.set(null);
        try {
            await this.api.deleteLinkedDevice(d.id);
            this.messageService.add({ severity: 'success', summary: this.t()('linked.removeDialog.done'), detail: this.t()('linked.removeDialog.doneDetail').replace('{name}', linkedDeviceDisplayName(d)) });
            this.showRemoveDialog.set(false);
            if (d.parentDeviceId) {
                this.router.navigate(['/device', d.parentDeviceId, 'linked-devices']);
            } else {
                this.router.navigate(['/dashboard']);
            }
        } catch (err: any) {
            this.removeError.set(err?.error?.detail || err?.message || this.t()('linked.removeDialog.failed'));
        } finally {
            this.removeSubmitting.set(false);
        }
    }

    async copyUniqueId() {
        const id = this.device()?.uniqueId;
        if (!id) return;
        try {
            await navigator.clipboard.writeText(id);
            this.messageService.add({ severity: 'success', summary: this.t()('linked.details.copied') });
        } catch {
            // Clipboard blocked (insecure context or permission): the ID is still selectable.
        }
    }
}
